<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2 } from 'lucide-vue-next';
import type { ClientQuickLink } from '~/types/models';

interface Props {
    open: boolean;
    drafts: ClientQuickLink[];
    saving: boolean;
}

defineProps<Props>();
const emit = defineEmits<{
    (event: 'update:open', value: boolean): void;
    (event: 'update:drafts', value: ClientQuickLink[]): void;
    (event: 'add'): void;
    (event: 'remove', id: string): void;
    (event: 'save'): void;
}>();
</script>

<template>
    <Sheet :open="open" @update:open="emit('update:open', $event)">
        <SheetContent class="w-full overflow-y-auto sm:max-w-xl">
            <SheetHeader class="text-left">
                <SheetTitle>Client quick links</SheetTitle>
                <SheetDescription>Add the useful links this account team reaches for regularly.</SheetDescription>
            </SheetHeader>

            <div class="space-y-3">
                <div v-for="link in drafts" :key="link.id" class="grid gap-3 rounded-xl border border-border/70 bg-muted/20 p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto_auto]">
                    <div class="space-y-1.5"><Label :for="`client-quick-link-label-${link.id}`">Label</Label><Input :id="`client-quick-link-label-${link.id}`" :model-value="link.label" placeholder="Client website" @update:model-value="emit('update:drafts', drafts.map((item) => item.id === link.id ? { ...item, label: String($event || '') } : item))" /></div>
                    <div class="space-y-1.5"><Label :for="`client-quick-link-url-${link.id}`">Link</Label><Input :id="`client-quick-link-url-${link.id}`" :model-value="link.url" placeholder="https://example.com" @update:model-value="emit('update:drafts', drafts.map((item) => item.id === link.id ? { ...item, url: String($event || '') } : item))" /></div>
                    <div class="space-y-1.5"><Label :for="`client-quick-link-color-${link.id}`">Colour</Label><input :id="`client-quick-link-color-${link.id}`" :value="link.color" type="color" class="h-10 w-14 rounded-md border border-input bg-background p-1" @input="emit('update:drafts', drafts.map((item) => item.id === link.id ? { ...item, color: ($event.target as HTMLInputElement).value } : item))"></div>
                    <div class="flex items-end"><Button type="button" variant="ghost" size="icon" class="rounded-full text-muted-foreground hover:text-destructive" title="Remove quick link" @click="emit('remove', link.id || '')"><Trash2 class="size-4" /></Button></div>
                </div>

                <div class="flex flex-wrap items-center justify-between gap-3">
                    <Button type="button" variant="outline" size="sm" @click="emit('add')"><Plus class="mr-2 size-4" />Add link</Button>
                    <div class="flex items-center gap-2"><Button type="button" variant="outline" :disabled="saving" @click="emit('update:open', false)">Cancel</Button><Button type="button" :disabled="saving" @click="emit('save')">{{ saving ? 'Saving...' : 'Save links' }}</Button></div>
                </div>
            </div>
        </SheetContent>
    </Sheet>
</template>
